<template>
	<div
		v-click-outside="closeDropdown"
		class="dropdown form-input"
		:class="{
			'is-open': isOpen,
			'is-mobile': isMobileWebApp,
			'is-animated': true,
			'yellow-border': isOpen && (searchable || isFreeTextEnabled),
			'is-free-text': isFreeTextEnabled,
			disabled: disabled,
		}"
	>
		<div
			v-show="(isOpen && searchable) || isFreeTextEnabled"
			class="select-wrapper"
			:class="{
				'has-active-selection': hasSelection && !disabled,
				'is-small': isSmall
			}"
		>
			<input
				v-if="isFreeTextEnabled"
				ref="input"
				:value="freeTextValue"
				class="search-input"
				:disabled="disabled"
				:placeholder="placeholder || labels.NO_SELECTION"
				@input="handleFreeTextInput($event.target.value)"
				@focus="handleInputFocus"
			>
			<input
				v-else
				ref="input"
				v-model="searchInput"
				class="search-input"
				:placeholder="placeholder || labels.NO_SELECTION"
				@focus="handleInputFocus"
			>
			<span
				class="caret"
			>
				<span>
					<Icon
						icon-name="e-remove"
						size="lg"
						:class="`remove-icon-color ${(isFreeTextEnabled ? freeTextValue : searchInput) ? '' : 'disabled'}`"
						@click="removeSelection"
					/>
				</span>
			</span>
		</div>
		<div
			v-show="!((isOpen && searchable) || isFreeTextEnabled)"
			class="select-wrapper"
			:class="{
				'has-active-selection': hasSelection && !disabled,
				'is-small': isSmall
			}"
			@click="toggleDropdown"
		>
			<slot
				name="selected-item"
				:selected-option="selectedOption"
			>
				<span
					v-if="!hasSelection"
					class="value-label"
				>
					{{ placeholder }}
				</span>
				<span class="value-label">{{ valueLabel }}</span>
			</slot>
			<span class="caret">
				<span>
					<Icon
						icon-name="down-arrow"
						class="text-white"
						:class="{ '-open': isOpen, disabled }"
						size="lg"
					/>
				</span>
			</span>
		</div>
		<!-- select options -->
		<transition name="list">
			<ul
				v-show="isOpen"
				class="dropdown-list is-single-select"
				:class="{ 'is-small': isSmall }"
			>
				<li
					v-for="option in filteredOptions"
					:key="option.id"
					:class="{'item-selected': isSelected(option), 'is-small': isSmall, disabled: option.disabled}"
					@click="handleSingleSelect(option)"
				>
					<slot
						name="item-list"
						:option="option"
						:selected-option="selectedOption"
					>
						{{ option.label }}
					</slot>
				</li>
				<li
					v-if="!hasSearchResults && noSearchResultsMessage"
					class="no-search-results"
				>
					<slot name="no-search-results">
						<span v-html="noSearchResultsMessage" />
					</slot>
				</li>
				<slot name="list-footer" />
			</ul>
		</transition>
	</div>
</template>

<script>
import Icon from '@components/Icon';
import { mapGetters } from 'vuex';

export default {
	name:       'Dropdown',
	components: { Icon },
	props:      {
		searchable: {
			type:    Boolean,
			default: false,
		},
		placeholder: {
			type: String,
		},
		options: {
			type:    Array,
			default: () => [],
		},
		value: {
			type:    [String, Number, Object],
			default: null,
		},
		isSmall: {
			type: Boolean,
		},
		disabled: {
			type:    Boolean,
			default: false,
		},
		allowFreeText: {
			type:    Boolean,
			default: false,
		},
		allowFreeTextWhenEmpty: {
			type:    Boolean,
			default: false,
		},
		noSearchResultsMessage: {
			type: String,
		},
	},
	data() {
		return {
			isOpen: false,
			labels: {
				ALL_OPTIONS:        '',
				NO_SELECTION:       '',
				MULTIPLE_SELECTION: '',
			},
			searchInput:   null,
			freeTextValue: '',
		};
	},
	computed: {
		...mapGetters({
			isMobileWebApp: 'app/isMobileWebApp',
		}),
		isFreeTextEnabled() {
			return this.allowFreeText || this.allowFreeTextWhenEmpty;
		},
		selectedOption() {
			return this.options.find(option => {
				if (option.id) {
					return option.id === this.value?.id;
				}
				return option.value === this.value;
			});
		},
		filteredOptions() {
			const searchValue = this.isFreeTextEnabled ? this.freeTextValue : this.searchInput;
			if (!searchValue) {
				return this.options;
			}
			return this.options.filter(option => option.id === 'none' || option.label.toLowerCase().includes(searchValue.toLowerCase()));
		},
		valueLabel() {
			if (this.isFreeTextEnabled) {
				return this.freeTextValue || this.labels.NO_SELECTION;
			}
			return !this.selectedOption ? this.labels.NO_SELECTION : this.selectedOption.label;
		},
		hasSelection() {
			if (this.isFreeTextEnabled) {
				return this.freeTextValue.length > 0;
			}
			return this.selectedOption !== undefined;
		},
		hasSearchResults() {
			return this.filteredOptions.length > 0;
		},
	},
	watch: {
		value: {
			handler() {
				this.syncFreeTextValue();
			},
			immediate: true,
		},
		isFreeTextEnabled() {
			this.syncFreeTextValue();
		},
	},
	methods: {
		syncFreeTextValue() {
			if (!this.isFreeTextEnabled) {
				return;
			}

			this.freeTextValue = typeof this.value === 'string'
				? this.value
				: (this.selectedOption?.label || '');
		},
		handleFreeTextInput(value) {
			this.freeTextValue = value;
			this.isOpen = true;
			this.$emit('input', value);
		},
		handleInputFocus() {
			if (this.disabled) {
				return;
			}
			this.isOpen = true;
		},
		removeSelection() {
			if (this.isFreeTextEnabled) {
				if (this.freeTextValue) {
					this.handleFreeTextInput('');
				}
				return;
			}
			if (this.searchInput) {
				this.searchInput = null;
			}
		},
		isSelected(option) {
			return this.selectedOption === option;
		},
		closeDropdown() {
			this.isOpen = false;
			if (!this.isFreeTextEnabled) {
				this.searchInput = null;
			}
		},
		toggleDropdown() {
			if (this.disabled) {
				return;
			}
			this.isOpen = !this.isOpen;
			if (this.isOpen && this.searchable) {
				this.$nextTick(() => {
					this.$refs.input.focus();
				});
			}
			this.$emit('dropdown-toggled');
		},
		selectOption(option) {
			if (option === this.selectedOption) {
				return;
			}
			if (this.isFreeTextEnabled) {
				this.freeTextValue = option.label;
			}
			this.$emit('input', option.value);
		},
		handleSingleSelect(option) {
			if (option.disabled) {
				return;
			}
			this.selectOption(option);
			this.closeDropdown();
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';
@import '@videotools/sass/_fonts';

.dropdown {
	user-select: none;
	position: relative;
	padding-top: 7px;
	padding-bottom: 7px;

	&.disabled {
		.select-wrapper {
			cursor: not-allowed;
		}
	}

	&.is-open {
		z-index: 10000;

		.dropdown-list {
			background-color: var(--color-grey-850);
		}

		&.yellow-border {
			border: 1px solid var(--color-hype-yellow);
		}
	}

	.search-input {
		color: var(--color-soft-white);
		flex-grow: 1;
	}
}

.dropdown.is-free-text {
	.search-input {
		cursor: text;
	}
}

.dropdown label {
	width: 100%;
	cursor: pointer;
}

.select-wrapper {
	overflow: hidden;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: space-between;
	color: var(--color-grey-600);
	min-height: 32px;
	box-sizing: border-box;

	&:not(.is-small) {
		min-height: 48px;
	}

	&.has-active-selection {
		color: var(--color-soft-white);
	}

	.dropdown-input {
		flex-grow: 1;
		color: var(--color-white);
	}

	.text-white {
		color: var(--color-white);
	}

	.remove-icon-color {
		color: var(--color-hype-yellow);

		&.disabled {
			color: var(--color-grey-500) !important;
		}
	}
}

.value-label {
	overflow: hidden;
	white-space: nowrap;
	text-overflow: ellipsis;
	max-width: 65%;

	.is-open & {
		color: var(--color-soft-white);
	}
}

.dropdown-list {
	position: absolute;
	width: calc(100% + 2px);
	top: 46px;
	right: -1px;
	z-index: 999;
	overflow-x: hidden;
	overflow-y: auto;
	border: 1px solid var(--color-grey-800);
	border-top-color: var(--color-hype-yellow);
	border-bottom-left-radius: 2px;
	border-bottom-right-radius: 2px;

	&:not(.is-small) {
		max-height: 62px;
		top: 62px;
	}

	.item-selected {
		background-color: var(--color-grey-700);
	}

	li {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding-left: 12px;
		height: 48px;
		color: var(--color-grey-300);

		&:hover {
			background: var(--color-grey-800);
		}

		.counter {
			font-size: 14px;
		}

		&:not(.is-small) {
			height: 64px;
		}

		.text-overflow {
			white-space: nowrap;
			overflow: hidden;
			text-overflow: ellipsis;
			width: calc(100% - 30px);
		}

		&.no-search-results {
			@include text-sm-2;
			color: var(--color-grey-300);
			padding: 8px 16px 8px 16px;
			height: initial;

			&:hover {
				background: initial;
			}
		}
	}
}

.is-animated .dropdown-list {
	transform-origin: top;
	transition: transform 0.2s cubic-bezier(0.55, 0.085, 0.32, 1);
}

.dropdown-list li {
	box-sizing: border-box;
}

.dropdown-list.is-single-select li:not(.no-search-results) {
	cursor: pointer;
}

.dropdown-list li.disabled {
	cursor: not-allowed;
	color: var(--color-grey-500);

	&:hover {
		background: var(--color-grey-850);
	}
}

.dropdown.is-open .dropdown-list {
	max-height: calc(85vh - 30px);

	&.is-small {
		max-height: 24vh;
	}

	&.is-large {
		width: 280px;
	}
}

.is-mobile .is-open .dropdown-list {
	max-height: 40vh;
}

.dropdown.is-open {
	border-radius: 2px 2px 0 0;
	border: 1px solid var(--color-grey-800);
	background: var(--color-grey-850);

	.caret .aicon {
		color: var(--color-hype-yellow);
	}
}

.caret {
	.icon,
	.aicon,
	.v2-icon {
		transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1) 0s;

		&.-open {
			transform: rotate(180deg);
		}

		&.disabled {
			color: var(--color-grey-600);
		}
	}
}

.list-enter,
.list-leave-to {
	transform: scaleY(0);
	will-change: transform;
}

.list-leave-active {
	transform-origin: bottom;
	background: var(--color-grey-050);
	opacity: 0.2;
	max-height: 24vh;
}

</style>
